import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_BASE_URL ?? "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

async function fetchStats() {
  const res = await fetch(`${API}/api/v2/cockpit/stats`, { headers: { Authorization: `Bearer ${getToken()}` } });
  return res.json();
}
async function fetchTransactions() {
  const res = await fetch(`${API}/api/v2/cockpit/transactions?limit=20`, { headers: { Authorization: `Bearer ${getToken()}` } });
  return res.json();
}

function refNum(txn) {
  if (txn.reference) return txn.reference;
  const prefix = txn.transactionType === 'SNPL' ? 'SNPL' : txn.transactionType === 'SNPN' ? 'SNPN' : 'TXN';
  return prefix + '-' + txn.id.replace(/-/g,'').substring(0,6).toUpperCase();
}

function TransactionModal({ txn, onClose }) {
  if (!txn) return null;
  const isSNPL = txn.transactionType === 'SNPL';
  const senderAmount = parseFloat(txn.senderAmount || 0);
  const exchangeFee = parseFloat(txn.variableFee || 0);
  const snplServiceFee = isSNPL ? parseFloat(txn.snplSettlement?.totalAmount || 0) - senderAmount : 0;
  const totalOwed = isSNPL ? senderAmount + snplServiceFee : senderAmount;
  const dueDate = txn.snplSettlement?.dueDate || null;

  const rows = [
    ["Reference", <span className="font-mono font-bold text-blue-700">{refNum(txn)}</span>],
    ["Transaction ID", <span className="font-mono text-xs break-all">{txn.id}</span>],
    ["Type", <span className={`px-2 py-0.5 rounded text-xs font-bold ${isSNPL ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>{txn.transactionType}</span>],
    ["User", `${txn.user?.firstName} ${txn.user?.lastName} (${txn.user?.email})`],
    ["Date", new Date(txn.createdAt).toLocaleString()],
    ...(dueDate ? [["Settlement due", <span className="font-bold text-red-600">{new Date(dueDate).toLocaleDateString('en-CA', {year:'numeric',month:'long',day:'numeric'})}</span>]] : []),
  ];

  const snplLimit = parseFloat(txn.user?.snplLimit || 250);
  const snplAvailable = parseFloat(txn.user?.snplAvailable != null ? txn.user.snplAvailable : snplLimit);
  const snplUsed = parseFloat(txn.user?.snplUsed != null ? txn.user.snplUsed : (snplLimit - snplAvailable));

  const amountRows = [
    ["Send amount", `$${senderAmount.toFixed(2)} ${txn.senderCurrency}`],
    ["Exchange fee (2.25%)", `$${exchangeFee.toFixed(2)} ${txn.senderCurrency}`],
    ...(isSNPL ? [
      ["SNPL service fee", `$${snplServiceFee.toFixed(2)} ${txn.senderCurrency}`],
      ["Total customer owes", <span className="font-bold text-blue-800">${totalOwed.toFixed(2)} {txn.senderCurrency}</span>],
    ] : []),
  ];

  const snplAccountRows = isSNPL ? [
    ["Credit limit", `$${snplLimit.toFixed(2)} ${txn.senderCurrency}`],
    ["Used (incl. this tx)", <span className="font-semibold text-orange-600">${snplUsed.toFixed(2)} {txn.senderCurrency}</span>],
    ["Available balance", <span className="font-bold text-green-700">${snplAvailable.toFixed(2)} {txn.senderCurrency}</span>],
  ] : [];

  const recipientRows = [
    ["Name", txn.recipientName],
    ["Phone", txn.recipientPhone],
    ["Country", txn.recipientCountry],
    ["Gets", <span className="font-bold text-green-700">{parseFloat(txn.recipientAmount||0).toLocaleString()} {txn.recipientCurrency}</span>],
    ["Rate", `1 ${txn.senderCurrency} = ${parseFloat(txn.exchangeRate||0).toFixed(4)} ${txn.recipientCurrency}`],
  ];

  const statusRows = [
    ["Overall", txn.overallStatus],
    ["Funding", `${txn.fundingStatus} ${txn.fundingProvider ? '('+txn.fundingProvider+')' : ''}`],
    ["Payout", `${txn.payoutStatus} ${txn.payoutProvider ? '('+txn.payoutProvider+')' : ''}`],
    ...(txn.snplSettlementId ? [["Settlement ID", <span className="font-mono text-xs break-all">{txn.snplSettlementId}</span>]] : []),
  ];

  const Section = ({ title, rows }) => (
    <div className="border-t pt-3 mt-3">
      <p className="font-semibold text-gray-700 mb-2">{title}</p>
      {rows.map(([label, val]) => (
        <div key={label} className="flex justify-between py-1">
          <span className="text-gray-500 w-1/3 shrink-0">{label}</span>
          <span className="text-right w-2/3">{val}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Transaction Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="text-sm space-y-1">
          {rows.map(([label, val]) => (
            <div key={label} className="flex justify-between border-b pb-1">
              <span className="text-gray-500 w-1/3 shrink-0">{label}</span>
              <span className="text-right w-2/3">{val}</span>
            </div>
          ))}
          <Section title="💰 Amount Breakdown" rows={amountRows} />
          {isSNPL && <Section title="💳 Customer SNPL Account" rows={snplAccountRows} />}
          <Section title="📦 Recipient" rows={recipientRows} />
          <Section title="🔁 Status" rows={statusRows} />
        </div>
      </div>
    </div>
  );
}

export default function CockpitDashboard() {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([fetchStats(), fetchTransactions()])
      .then(([s, t]) => {
        if (s.ok) setStats(s.stats || s.data || s);
        if (t.ok) setTransactions(t.transactions || t.data || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-gray-500">Loading dashboard...</div>;
  if (error) return <div className="p-8 text-red-500">Error: {error}</div>;

  const statCards = [
    { label: "Total Users", value: stats?.total_users ?? 0, icon: "👥", color: "bg-blue-50 border-blue-200" },
    { label: "Total Transactions", value: stats?.total_transactions ?? 0, icon: "💸", color: "bg-green-50 border-green-200" },
    { label: "SNPN Transactions", value: stats?.snpn_count ?? 0, icon: "⚡", color: "bg-purple-50 border-purple-200" },
    { label: "SNPL Transactions", value: stats?.snpl_count ?? 0, icon: "📅", color: "bg-yellow-50 border-yellow-200" },
    { label: "Total Volume (CAD)", value: `$${Number(stats?.total_volume ?? 0).toLocaleString('en-CA', {minimumFractionDigits:2})}`, icon: "💰", color: "bg-emerald-50 border-emerald-200" },
    { label: "Active SNPL Settlements", value: stats?.active_settlements ?? 0, icon: "⏳", color: "bg-orange-50 border-orange-200" },
    { label: "Outstanding SNPL (CAD)", value: `$${Number(stats?.outstanding_snpl ?? 0).toLocaleString('en-CA', {minimumFractionDigits:2})}`, icon: "📋", color: "bg-red-50 border-red-200" },
    { label: "New Users (7d)", value: stats?.users_last_7days ?? 0, icon: "🆕", color: "bg-indigo-50 border-indigo-200" },
  ];

  const statusColor = (s) => {
    if (!s) return "text-gray-500";
    const v = s.toLowerCase();
    if (v === "completed") return "text-green-600 font-semibold";
    if (v === "pending") return "text-yellow-600 font-semibold";
    if (v === "failed") return "text-red-600 font-semibold";
    return "text-gray-600";
  };

  const displayAmount = (t) => {
    const sender = parseFloat(t.senderAmount || 0);
    const fee = parseFloat(t.totalFee || 0);
    if (t.transactionType === 'SNPL') {
      const snplTotal = parseFloat(t.snplSettlement?.totalAmount || (sender + fee));
      const snplFee = snplTotal - sender;
      return { amount: `$${snplTotal.toFixed(2)}`, sub: `($${sender.toFixed(2)} + $${snplFee.toFixed(2)} fee)` };
    }
    return { amount: `$${sender.toFixed(2)}`, sub: `(fee: $${fee.toFixed(2)})` };
  };

  return (
    <div className="p-6 space-y-6">
      {selected && <TransactionModal txn={selected} onClose={() => setSelected(null)} />}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">📊 ZoomPay Operations Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Live platform metrics · {new Date().toLocaleString()}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.color}`}>
            <div className="text-2xl mb-1">{c.icon}</div>
            <div className="text-2xl font-bold text-gray-900">{c.value}</div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="font-bold text-lg">💸 Recent Transactions</h2>
          <span className="text-xs text-gray-500">Last 20 · Click View for details</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["Date","Reference","User","Type","Amount","Recipient","Corridor","Status",""].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-gray-600 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {transactions.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No transactions yet</td></tr>
              )}
              {transactions.map((t) => {
                const { amount, sub } = displayAmount(t);
                return (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(t.createdAt).toLocaleDateString('en-CA')}</td>
                    <td className="px-4 py-3 font-mono text-xs text-blue-700 font-bold">{refNum(t)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{t.user?.firstName} {t.user?.lastName}</div>
                      <div className="text-xs text-gray-400">{t.user?.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${t.transactionType === 'SNPL' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                        {t.transactionType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{amount} {t.senderCurrency}</div>
                      <div className="text-xs text-gray-400">{sub}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{t.recipientName}</div>
                      <div className="text-xs text-gray-400">{t.recipientPhone}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{t.corridor || `${t.senderCurrency}→${t.recipientCurrency}`}</td>
                    <td className={`px-4 py-3 ${statusColor(t.status)}`}>{t.status?.toUpperCase()}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(t)} className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
